/**
 * IXVerify v2 — module shell layout (ixv2 WP-2.3).
 *
 * Standard platform AppBar (h-12, the system's only gradient) with the
 * SiteSelector in the module-owned right slot, an underline tab nav for the
 * module's pages (design-system Tabs recipe — 44px touch targets collapsing
 * at sm:), and the content area rendering into an <Outlet/> so page hops
 * never remount the shell (the eReg Phase-1 pattern).
 *
 * The Audit log tab (WP-4.2) renders for administrators only — the backend
 * enforces the same wall (403 IXV_ADMIN_REQUIRED) regardless, and the page
 * itself shows a denied state if reached directly.
 */
import { NavLink, Outlet } from 'react-router-dom';
import { Users, FlaskConical, LayoutDashboard, History, Settings, ScrollText, Gauge } from 'lucide-react';
import { AppBar } from '../../ui';
import SiteSelector from '../../components/SiteSelector';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from './shared/NotificationBell';

const TABS = [
  { to: '/ixverify', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/ixverify/subjects', label: 'Subjects', icon: Users },
  { to: '/ixverify/studies', label: 'Studies', icon: FlaskConical },
  { to: '/ixverify/history', label: 'History', icon: History },
  { to: '/ixverify/settings', label: 'Settings', icon: Settings },
];

const ADMIN_TABS = [
  { to: '/ixverify/accuracy', label: 'Accuracy', icon: Gauge, end: false },
  { to: '/ixverify/audit', label: 'Audit log', icon: ScrollText, end: false },
];

export default function Layout() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const tabs = isAdmin || isSuperAdmin ? [...TABS, ...ADMIN_TABS] : TABS;
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AppBar
        right={
          <>
            <NotificationBell />
            <SiteSelector />
          </>
        }
      />

      {/* Module tab nav — underline tabs recipe */}
      <nav aria-label="IXVerify sections" className="border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-[1700px] gap-1 overflow-x-auto">
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-2 ${
                  isActive
                    ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`
              }
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="min-w-0 px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5">
        <div className="mx-auto w-full max-w-[1700px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
